import packageInfo from "../../package.json";

export const CURRENT_APP_VERSION = packageInfo.version;

export type AppUpdate = {
  fromOtherTab?: boolean;
  registration?: ServiceWorkerRegistration;
  version?: string;
};

type UpdateListener = (update: AppUpdate) => void;

const listeners = new Set<UpdateListener>();
let pendingUpdate: AppUpdate | null = null;
const tabId = `${Date.now()}-${Math.random()}`;
const updateChannel =
  "BroadcastChannel" in window
    ? new BroadcastChannel("app-update-channel")
    : null;

export const subscribeToAppUpdates = (listener: UpdateListener) => {
  listeners.add(listener);
  if (pendingUpdate) listener(pendingUpdate);
  return () => listeners.delete(listener);
};

export const notifyAppUpdate = (update: AppUpdate) => {
  pendingUpdate = { ...(pendingUpdate ?? {}), ...update };
  listeners.forEach((listener) => listener(pendingUpdate as AppUpdate));
};

updateChannel?.addEventListener("message", (event) => {
  if (
    event.data?.type === "APP_UPDATE_ACCEPTED" &&
    event.data.source !== tabId
  ) {
    notifyAppUpdate(
      event.data.version
        ? { version: event.data.version, fromOtherTab: true }
        : { fromOtherTab: true },
    );
  }
});

export const clearPendingAppUpdate = () => {
  pendingUpdate = null;
};

const runtimeReloadKey = (version?: string) =>
  version ? `app-update-reload:${version}` : null;

export const hasAttemptedRuntimeReload = (version?: string) => {
  const key = runtimeReloadKey(version);
  return key ? sessionStorage.getItem(key) === "true" : false;
};

export const activateAppUpdate = async (
  registration?: ServiceWorkerRegistration,
  options: { broadcast?: boolean } = {},
) => {
  const activeRegistration =
    registration ??
    ("serviceWorker" in navigator
      ? // eslint-disable-next-line compat/compat
        await navigator.serviceWorker.getRegistration()
      : undefined);
  const waitingWorker = activeRegistration?.waiting;

  if (!waitingWorker) {
    const key = runtimeReloadKey(pendingUpdate?.version);
    if (key) sessionStorage.setItem(key, "true");
    window.location.reload();
    return;
  }

  if (options.broadcast !== false) {
    updateChannel?.postMessage({
      type: "APP_UPDATE_ACCEPTED",
      source: tabId,
      version: pendingUpdate?.version,
    });
  }
  // eslint-disable-next-line compat/compat
  navigator.serviceWorker.addEventListener(
    "controllerchange",
    () => window.location.reload(),
    { once: true },
  );
  waitingWorker.postMessage({ type: "SKIP_WAITING" });
  clearPendingAppUpdate();
};

const isNewerVersion = (serverVersion: string, currentVersion: string) => {
  const serverParts = serverVersion.split(".").map(Number);
  const currentParts = currentVersion.split(".").map(Number);

  if (
    serverParts.some(Number.isNaN) ||
    currentParts.some(Number.isNaN) ||
    serverParts.length !== 3 ||
    currentParts.length !== 3
  ) {
    return serverVersion !== currentVersion;
  }

  return serverParts.some(
    (part, index) =>
      part > currentParts[index] &&
      serverParts.slice(0, index).every((value, prefixIndex) =>
        value === currentParts[prefixIndex],
      ),
  );
};

export const checkRuntimeVersion = async () => {
  try {
    const response = await fetch(`/meta.json?t=${Date.now()}`, {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) return;

    const { version } = (await response.json()) as { version?: string };
    if (version && isNewerVersion(version, CURRENT_APP_VERSION)) {
      notifyAppUpdate({ version });
    }
  } catch {
    // A version check must never affect normal application availability.
  }
};
